import {ChangeDetectionStrategy, Component, Inject, signal, TemplateRef, ViewChild} from '@angular/core';
import {FormBuilder, Validators} from '@angular/forms';
import {DiscountSyncDefaultSettings, ShopifyAuthorizationDetail} from '../../../core/dtos/api';
import {combineLatest, of} from 'rxjs';
import {DashboardApiService} from '../../../core/services/api/dashboard-api.service';
import {switchMap, take} from 'rxjs/operators';
import {UserSettingsApiService} from '../../../core/services/api/user-settings-api.service';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {DiscountApiService} from '../../../core/services/api/discount-api.service';
import {SnackBarService} from '../../../core-services/snack-bar/snack-bar.service';
import {HttpErrorResponse} from '@angular/common/http';
import {environment} from '../../../../environments/environment';

type DiscountAffiliateDefault = 'noAffiliates' | 'allAffiliates';
export interface ShopifySyncDialogData {
  shopifyDiscountsSyncing: boolean;
  automaticDiscountsAllAffiliates: boolean;
  couponDiscountsAllAffiliates: boolean;
  selectedSyncingStoreName: string | null;
  shopifyShopNames: ShopifyAuthorizationDetail[];
}

@Component({
  selector: 'app-shopify-sync-dialog',
  templateUrl: './shopify-sync-dialog.component.html',
  styleUrl: './shopify-sync-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ShopifySyncDialogComponent {
  @ViewChild('discardUnsavedChangesDialog', {static: true}) discardUnsavedChangesDialog: TemplateRef<any>;
  constructor(@Inject(MAT_DIALOG_DATA) public data: ShopifySyncDialogData, private fb: FormBuilder, private dashboardApiService: DashboardApiService,
    private userSettingsApiService: UserSettingsApiService, private discountApiService: DiscountApiService, private matDialog: MatDialog,
    private matDialogRef: MatDialogRef<ShopifySyncDialogComponent>, private snackBarService: SnackBarService) {
  }
  protected readonly environment = environment;
  isTheSameFromInitial = signal(true);
  hasSubmitBeenClicked = signal(false);
  controls = {
    syncSlideToggle: this.fb.control(false),
    automaticDiscountsRadioButton: this.fb.control<DiscountAffiliateDefault>('allAffiliates'),
    couponCodesRadioButton: this.fb.control<DiscountAffiliateDefault>('noAffiliates'),
    shopNameSelect: this.fb.control(null, Validators.required)
  };
  formFieldGroup = this.fb.group(this.controls);
  updateFormFields$Sub = of(this.data).subscribe((data) => {
    if (data?.shopifyDiscountsSyncing) {
      this.controls.syncSlideToggle.setValue(data.shopifyDiscountsSyncing);
      this.controls.shopNameSelect.disable();
    }
    if (data?.automaticDiscountsAllAffiliates !== null) {
      this.controls.automaticDiscountsRadioButton.setValue(data.automaticDiscountsAllAffiliates ? 'allAffiliates' : 'noAffiliates');
    }
    if (data?.couponDiscountsAllAffiliates !== null) {
      this.controls.couponCodesRadioButton.setValue(data.couponDiscountsAllAffiliates? 'allAffiliates' : 'noAffiliates');
    }
    if (data?.selectedSyncingStoreName) {
      this.controls.shopNameSelect.setValue(data.selectedSyncingStoreName);
    }
  });
  updateIsSameFromInitial$Sub = this.formFieldGroup.valueChanges.pipe(takeUntilDestroyed()).subscribe(formFieldGroup => {
    let shopifyDiscountSyncing = formFieldGroup.syncSlideToggle;
    let automaticDiscountsAllAffiliates = formFieldGroup.automaticDiscountsRadioButton === 'allAffiliates';
    let couponDiscountsAllAffiliates = formFieldGroup.couponCodesRadioButton === 'allAffiliates';
    // Only enable button if the syncSlideToggle is different from initial or if syncSlideToggle is true and there is a change in one of the radio
    // buttons.
    if (shopifyDiscountSyncing !== this.data.shopifyDiscountsSyncing) {
      this.isTheSameFromInitial.set(false);
    } else if(shopifyDiscountSyncing === true && (automaticDiscountsAllAffiliates !== this.data.automaticDiscountsAllAffiliates
        || couponDiscountsAllAffiliates !== this.data.couponDiscountsAllAffiliates)) {
      this.isTheSameFromInitial.set(false);
    } else {
      this.isTheSameFromInitial.set(true);
    }
  });

  onCancelClick() {
    if (this.isTheSameFromInitial()) {
      this.matDialogRef.close(false);
      return;
    }

    let discardDialogRef: MatDialogRef<typeof this.discardUnsavedChangesDialog, boolean> = this.matDialog.open(this.discardUnsavedChangesDialog,);
    discardDialogRef.afterClosed().subscribe((close) => {
      if (close) {
        this.matDialogRef.close(false);
      }
    });
  }

  onSaveButtonClick() {
    this.hasSubmitBeenClicked.set(true);
    let defaultSettings: DiscountSyncDefaultSettings = {
      automatic_discounts_all_affiliates: this.controls.automaticDiscountsRadioButton.value === 'allAffiliates',
      coupon_discounts_all_affiliates: this.controls.couponCodesRadioButton.value === 'allAffiliates',
      selected_syncing_store_name: this.controls.shopNameSelect.value
    };
    combineLatest([this.dashboardApiService.currentAccount$, of(defaultSettings)]).pipe(
      switchMap(([currentAccount, defaultSettingsValue]) => this.userSettingsApiService.putShopifySyncDefaults(currentAccount.entity_id, defaultSettingsValue)),
      switchMap((shopifySyncResponse) => {
        if (this.controls.syncSlideToggle.value !== this.data.shopifyDiscountsSyncing) {
          return this.dashboardApiService.currentAccount$.pipe(
            switchMap(currentAccount => this.discountApiService.updateDiscountsShopifySync(currentAccount.entity_id, this.controls.syncSlideToggle.value))
          );
        }
        return of(null);
      }),
      take(1)
    ).subscribe({
      next: (discountShopifySyncResponse) => {
        if (discountShopifySyncResponse !== null) {
          if (this.controls.syncSlideToggle.value === true) {
            this.snackBarService.openSnackBar('Shopify Sync is ON', null, 4000);
          } else {
            this.snackBarService.openSnackBar('Shopify Sync is OFF');
          }
        } else {
          this.snackBarService.openSnackBar('Shopify Sync updated successfully');
        }
        this.matDialogRef.close(true);
      },
      error: (httpErrorResponse: HttpErrorResponse) => {
        this.snackBarService.openErrorSnackBar(httpErrorResponse.error.error);
      }
    });
  }
}
